import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { AllIconsComponent } from "../../all-icons/all-icons.component";
import { OverlayPopUpComponent } from "../../overlayed-pop-up/overlay-pop-up.component";
import { AsyncPipe } from '@angular/common';

@Component({
  selector: 'app-input-autocomplete',
  templateUrl: './input-autocomplete.component.html',
  styleUrls: ['./input-autocomplete.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    multi: true,
    useExisting: forwardRef(() => InputAutocompleteComponent),
  }],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AllIconsComponent,
    OverlayPopUpComponent,
    FormsModule,
    AsyncPipe,
  ],
})
export class InputAutocompleteComponent implements OnChanges, ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);


  /**
   * TL&DR: if you want to set only one simple array (e.x. [1,2,3,4,5]): set only keys in map.
   *
   * @Input data is a map which contains key-value relation.
   * Example: I have complex objects. I want to choose one by name and get it's ID back.
   * To do so I provide e.x. follow map-object:
   * complexData = new Map<string, number>([
   ['First Company', 58], ['Second Company', 76], ['Third Company', 130],
   ]);
   */
  @Input() data: Map<any, any> | any[] = new Map<any, any>([]);
  @Input() field = '';
  @Input() title = '';
  @Input() inputID = (Math.random() + 1).toString(36).substring(7);
  @Input() placeHolder = '';
  @Input() isShrinked = false;
  @Input() errorProperties = new ErrorProperties(); // object with : required - for * , errorState - set true for red style and bottomText
  @Input() selectedObject: string;
  @Input() dataQA = 'input-autocomplete';

  /*
   * Additional CSS class applied to the input element for customization.
   *
   * Example:
   * <app-input-autocomplete [additionalClass]="'border-main-portal-500'" />
   * This applies the "border-main-portal-500" class to the input,
   * giving it the main-portal-500 border color.
   */
  @Input() additionalClass: string;

  @Output() valueEmitter = new EventEmitter<any>();
  isSimpleArray = true;
  isDisabled = false;

  dataKeys: string[];
  dataValues: string[];

  searchInputValue$ = new BehaviorSubject<string>('');
  filteredData: Observable<any[]>;

  updateAndEmitSelectedValue(value): void {
    this.selectedObject = this.isSimpleArray ? value : value[this.field];
    this.searchInputValue$.next(this.selectedObject);
    const valueToEmit = this.dataValues ? (this.data as Map<any, any>).get(value) : this.selectedObject;
    this.valueEmitter.emit(valueToEmit);
    this.onChange(valueToEmit);
  }

  setFilteredData(): void {
    this.filteredData = this.searchInputValue$
      .asObservable().pipe(
        startWith(''),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        map((value) => this.filter(value)),
      );
  }

  emitValue(value): void {
    this.searchInputValue$.next(value);
    this.valueEmitter.emit(value);
  }

  inputChange(value): void {
    this.searchInputValue$.next(value);
    this.onChange(value);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.field) {
      this.isSimpleArray = (this.field === '');
    }
    if (changes.data && this.data) {
      if (this.data instanceof Map) {
        this.dataKeys = Array.from(this.data.keys());
        this.dataValues = Array.from(this.data.values());
      } else {
        this.dataKeys = this.data;
      }
      this.setFilteredData();
    }
    if (changes.selectedObject && changes.selectedObject.currentValue !== changes.selectedObject.previousValue) {
      this.searchInputValue$.next(changes.selectedObject.currentValue ?? '');
    }
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  writeValue(value): void {
  }

  /**
   * Update form when DOM element value changes (view => model)
   */
  registerOnChange(fn: any): void {
    // Store the provided function as an internal method.
    this.onChange = fn;
  }

  /**
   * Update form when DOM element is blurred (view => model)
   */
  registerOnTouched(fn: any): void {
    // Store the provided function as an internal method.
    this.onTouched = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChange(value): void { }

  onTouched(): void { }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }

  private filter(value): any[] {
    const filterValue = value?.toString().toLowerCase();
    return this.dataKeys?.filter((el) => {
      const property = this.isSimpleArray ? el : el[this.field];
      return property?.toString().toLowerCase().includes(filterValue);
    });
  }
}

export class ErrorProperties {
  required = false;
  errorState = false;
  bottomText = '';
}
